import {
  RailConfigType,
  type RecipientTarget,
  type SwappedFormOfPayment,
  getFops,
} from '@funkit/api-base'
import {
  FUNKIT_CONNECT_CHECKOUT_NATIVE_CURRENCY_ADDRESS,
  solanaChain,
} from '@funkit/chains'
import {
  DEFAULT_FIAT_CURRENCY,
  FOPS_STALE_TIME,
  buildSwappedThirdPartyPaymentConfigs,
  getConfiguredMinDepositUsd,
  getDepositAddressForChain,
  getMinTransferValueEur,
  sortFops,
  useSwappedDefaultCurrencyQuery,
} from '@funkit/connect-core'
import { useDynamicConfig } from '../../statsig/useDynamicConfig'
import { useTheme } from '@shopify/restyle'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type {
  DepositCheckoutConfig,
  DepositClientMetadata,
  DepositInitialAmount,
} from '../../flow/depositFlowConfig'
import { useUserIpInfo } from '../../geo/useUserIpInfo'
import { useFunkitIdentity } from '../../identity/IdentityContext'
import { logger } from '../../logging/logger'
import { buildSwappedThemeQueryParamsRN } from '../../swapped/buildSwappedTheme'
import type { FunkitTheme } from '../../theme/toRestyleTheme'
import { excludeUnsupportedPlatformFops } from '../fops'
import { useFiatExchangeRates } from './useFiatExchangeRates'
import { useTransferInit } from './useTransferInit'

// Swapped enumerates region-specific payment methods by country, so the /fops
// call needs *a* country. We send the user's real country (resolved from their
// IP via {@link useUserIpInfo}), falling back to this default while the geo
// lookup is in flight or if it fails — so the cash list still populates.
//
// TODO(connect-rn): per-region geoblocking. Web also compares `blockedcountries`
// (Statsig) against the resolved country and disables/hides methods for blocked
// regions (`useFunkitUserIp().isUserGeoblocked` →
// `PaymentMethodDisabledBadge reason="geoblocked"`). This wires the country in;
// the per-method gating is the remaining step.
const DEFAULT_COUNTRY_CODE = 'US'

export interface UseFopsParams {
  /**
   * Destination chain/asset — drives the deposit recipient + FOP query. Its
   * optional `getMinDepositUSD` feeds the fiat (Swapped) buy minimum.
   */
  checkoutConfig: DepositCheckoutConfig | undefined
  dynamicRoutingId?: string
  /** Active flow's checkoutId — see {@link useTransferInit}. */
  checkoutId?: string
  /** Host-supplied correlation ids — see {@link useTransferInit}. */
  clientMetadata?: DepositClientMetadata
  initialAmount?: DepositInitialAmount
  lockAmount?: boolean
  enabled?: boolean
}

export interface UseFopsResult {
  /**
   * The user's saved forms of payment (e.g. "Mastercard (…3869)"). Rendered at
   * the top of the Add money list, above the Crypto row — mirroring web, where
   * saved FOPs sort first.
   */
  savedFops: SwappedFormOfPayment[]
  /** Generic forms of payment (Card / Apple Pay / Revolut Pay / Bank Transfer). */
  genericFops: SwappedFormOfPayment[]
  isLoading: boolean
  isFetching: boolean
  /** The FOPs fetch failed (network / API). Drives the Add money error state. */
  isError: boolean
  /** Re-run the FOPs fetch (the Add money "Try again" action). */
  refetch: () => void
  /** Whether Swapped (cash) methods are offered at all. */
  isSwappedEnabled: boolean
  /**
   * The /fops fetch has every input it needs to run (Swapped on, recipient +
   * target resolved) — only then can a refetch change the result. Gates the
   * Add money "Try again".
   */
  canRefetch: boolean
}

/**
 * Thin RN port of `@funkit/connect`'s `useFops`: fetches the Swapped forms of
 * payment (`getFops`) that populate the "Use Cash" tab. Identity comes from
 * {@link useFunkitIdentity}; the deposit recipient is derived from
 * {@link useTransferInit} + the core `getDepositAddressForChain`.
 *
 * Theme, buy minimum, and region currency reach web parity (see the inline
 * notes below). V1 still omits web's per-region geoblocking and `validateFops`.
 */
export function useFops({
  checkoutConfig,
  dynamicRoutingId,
  checkoutId,
  clientMetadata,
  initialAmount,
  lockAmount,
  enabled = true,
}: UseFopsParams): UseFopsResult {
  const isSwappedEnabled = !!useDynamicConfig('enableswappeddeposit')
  const input = useFopsQueryInput({
    checkoutConfig,
    dynamicRoutingId,
    checkoutId,
    clientMetadata,
    initialAmount,
    lockAmount,
    enabled: enabled && isSwappedEnabled,
  })
  const query = useDoFetchFops(input)

  // The region-currency / rate lookups keep the fetch disabled, and a disabled
  // query's `isLoading` reads false — indistinguishable from "settled, no cash
  // methods". Cover those windows too, or callers paint a cash-less list before
  // /fops has even been requested.
  const isLoading =
    isSwappedEnabled &&
    (input.isDependencyLoading || (input.enabled && query.isLoading))

  return {
    savedFops: query.data?.savedFormsOfPayments ?? [],
    genericFops: query.data?.genericFormsOfPayments ?? [],
    isLoading,
    isFetching: input.isTransferInitFetching,
    isError: query.isError,
    refetch: () => void query.refetch(),
    isSwappedEnabled,
    canRefetch: input.enabled,
  }
}

/**
 * Resolves the /fops request into a query key — identity, region (country +
 * fiat currency), Swapped theme, deposit recipient, EUR buy minimum — plus
 * whether the fetch may run and which upstream lookups are still pending.
 *
 * The key and the loading flags live together on purpose: a dependency added to
 * the key has to declare its loading state right here, so it can't be forgotten
 * when `useFops` composes `isLoading`.
 */
function useFopsQueryInput({
  checkoutConfig,
  dynamicRoutingId,
  checkoutId,
  clientMetadata,
  initialAmount,
  lockAmount,
  enabled,
}: {
  checkoutConfig: DepositCheckoutConfig | undefined
  dynamicRoutingId: string | undefined
  checkoutId: string | undefined
  clientMetadata: DepositClientMetadata | undefined
  initialAmount: DepositInitialAmount | undefined
  lockAmount: boolean | undefined
  enabled: boolean
}) {
  const { apiKey, userId, prefillFiatEmail } = useFunkitIdentity()
  const fopPriority = useDynamicConfig('foppriority')

  // Real country from the user's IP; default while it loads / if it fails.
  const { userIpInfo } = useUserIpInfo()

  // Region-default fiat currency (web parity); falls back to USD. Resolved from
  // the *real* IP country (not the DEFAULT_COUNTRY_CODE fallback above) so a
  // non-US user isn't briefly quoted in USD. Gating /fops on its loading state
  // avoids an initial USD fetch that refetches once the region currency resolves.
  const {
    data: fiatCurrencyCode = DEFAULT_FIAT_CURRENCY,
    isLoading: isFiatCurrencyCodeLoading,
  } = useSwappedDefaultCurrencyQuery({
    apiKey,
    countryCode: userIpInfo?.alpha2,
    logger,
  })

  // Loading-state theme baked into the returned embeddedFlowUrls (web parity).
  const theme = useTheme<FunkitTheme>()
  const themeConfig = useMemo(
    () => buildSwappedThemeQueryParamsRN(theme),
    [theme],
  )

  const {
    transferInit,
    isLoading: isTransferInitLoading,
    isFetching: isTransferInitFetching,
  } = useTransferInit({
    checkoutConfig,
    dynamicRoutingId,
    checkoutId,
    clientMetadata,
    enabled,
  })

  const targetAsset = checkoutConfig?.targetAsset
  const targetChain = checkoutConfig?.targetChain
  const recipientAddress = targetChain
    ? getDepositAddressForChain(Number(targetChain), transferInit)
    : undefined

  const { defaultMinAmount, isLoading: isExchangeRatesLoading } =
    useDefaultMinAmountEur({ checkoutConfig, dynamicRoutingId, enabled })

  const baseCurrencyAmount = initialAmount
    ? Number(initialAmount.value)
    : undefined
  const baseCurrencyCode = initialAmount?.currency

  return {
    // `prefillFiatEmail` is intentionally kept out of the key (web parity):
    // it's read at fetch time, so post-mount changes don't refetch.
    queryKey: [
      'fops',
      {
        apiKey,
        userId,
        recipientAddress,
        solanaAddress: transferInit?.solanaAddr,
        targetAsset,
        targetChain,
        // fallback to DEFAULT_COUNTRY_CODE, do not use it in useSwappedDefaultCurrencyQuery
        countryCode: userIpInfo?.alpha2 || DEFAULT_COUNTRY_CODE,
        fiatCurrencyCode,
        fopPriority,
        themeConfig,
        defaultMinAmount,
        baseCurrencyAmount,
        baseCurrencyCode,
        lockAmount: lockAmount === true,
      },
    ] as const,
    prefillFiatEmail,
    enabled:
      enabled &&
      !!apiKey &&
      !!targetAsset &&
      !!targetChain &&
      !!recipientAddress &&
      !!transferInit &&
      !isFiatCurrencyCodeLoading &&
      !isExchangeRatesLoading,
    isDependencyLoading:
      isTransferInitLoading ||
      isFiatCurrencyCodeLoading ||
      isExchangeRatesLoading,
    isTransferInitFetching,
  }
}

/**
 * Swapped enforces its own minimum inside the widget; we hand it the same floor
 * web sends (shared `getMinTransferValueEur`) — the chain transfer minimum plus
 * the config's `getMinDepositUSD`, converted to EUR. Callers gate /fops on
 * `isLoading` so the first request already carries the real EUR floor rather
 * than a 1:1 estimate that later churns the query key.
 */
function useDefaultMinAmountEur({
  checkoutConfig,
  dynamicRoutingId,
  enabled,
}: {
  checkoutConfig: DepositCheckoutConfig | undefined
  dynamicRoutingId: string | undefined
  enabled: boolean
}) {
  const minTransferLimits = useDynamicConfig('mintokentransfervalue')
  const { data: exchangeRates, isLoading } = useFiatExchangeRates(enabled)

  const targetChain = checkoutConfig?.targetChain
  const defaultMinAmount = getMinTransferValueEur(
    targetChain ? Number(targetChain) : undefined,
    minTransferLimits,
    getConfiguredMinDepositUsd({
      targetChain,
      targetAsset: checkoutConfig?.targetAsset,
      dynamicRoutingId,
      getMinDepositUSD: checkoutConfig?.getMinDepositUSD,
    }),
    exchangeRates,
  )

  return { defaultMinAmount, isLoading }
}

function buildInitialAmountRailConfig({
  baseCurrencyAmount,
  baseCurrencyCode,
  lockAmount,
}: {
  baseCurrencyAmount: number | undefined
  baseCurrencyCode: string | undefined
  lockAmount: boolean
}) {
  if (baseCurrencyAmount === undefined || baseCurrencyCode === undefined) {
    return {}
  }

  return {
    baseCurrencyAmount,
    baseCurrencyCode,
    ...(lockAmount ? { lockAmount: true, lockBaseCurrency: true } : {}),
  }
}

/** Runs the /fops fetch off the key built by {@link useFopsQueryInput}. */
function useDoFetchFops({
  queryKey,
  prefillFiatEmail,
  enabled,
}: ReturnType<typeof useFopsQueryInput>) {
  return useQuery({
    queryKey,
    queryFn: async ({ queryKey: [_, params] }) => {
      const { recipientAddress, targetAsset, targetChain, fopPriority } = params
      if (!recipientAddress || !targetChain || !targetAsset) {
        return { genericFormsOfPayments: [], savedFormsOfPayments: [] }
      }
      const destinationToken = { chainId: targetChain, address: targetAsset }
      // Swapped recommends a Solana fallback recipient when one was minted.
      const recipientTargets: RecipientTarget[] = [
        { recipientAddress, destinationToken },
        ...(params.solanaAddress
          ? [
              {
                recipientAddress: params.solanaAddress,
                destinationToken: {
                  chainId: solanaChain.id.toString(),
                  address: FUNKIT_CONNECT_CHECKOUT_NATIVE_CURRENCY_ADDRESS,
                },
              },
            ]
          : []),
      ]

      const thirdPartyPaymentConfigs = buildSwappedThirdPartyPaymentConfigs({
        prefillFiatEmail,
        themeConfig: params.themeConfig,
        logTag: 'connect-rn:useFops',
        logger,
      })
      const checkoutLimitsCriteria = {
        defaultMinAmount: params.defaultMinAmount,
      }

      const response = await getFops({
        apiKey: params.apiKey,
        logger,
        params: {
          recipientAddress,
          userId: params.userId,
          destinationToken,
          fiatCurrencyCode: params.fiatCurrencyCode,
          countryCode: params.countryCode,
          recipientTargets,
          railConfigs: [
            {
              type: RailConfigType.FIAT_TO_CRYPTO,
              userId: params.userId,
              countryCode: params.countryCode,
              fiatCurrencyCode: params.fiatCurrencyCode,
              recipientTargets,
              ...buildInitialAmountRailConfig(params),
              thirdPartyPaymentConfigs,
              checkoutLimitsCriteria,
            },
          ],
          thirdPartyPaymentConfigs,
          checkoutLimitsCriteria,
        },
      })

      return {
        genericFormsOfPayments: sortFops(
          excludeUnsupportedPlatformFops(response.genericFormsOfPayments),
          fopPriority,
        ),
        savedFormsOfPayments: sortFops(
          excludeUnsupportedPlatformFops(response.savedFormsOfPayments),
          fopPriority,
        ),
      }
    },
    enabled,
    staleTime: FOPS_STALE_TIME,
    refetchOnWindowFocus: false,
  })
}
